
import React from 'react';
import { mdx } from '@mdx-js/react';


export const frontMatter = {
	id: 'client',
	title: 'Client Data'
};
export const contentTitle = undefined;
export const metadata = {
  "unversionedId": "api-reference/client",
  "id": "api-reference/client",
  "isDocsHomePage": false,
  "title": "Client Data",
  "description": "All client information is stored under the tenant of the API_KEY owner.  Furthermore, all changes to",
  "source": "@site/docs/api-reference/client.md",
  "sourceDirName": "api-reference",
  "slug": "/api-reference/client",
  "permalink": "/docs/api-reference/client",
  "tags": [],
  "version": "current",
  "frontMatter": {
    "id": "client",
    "title": "Client Data"
  },
  "sidebar": "docs",
  "previous": {
    "title": "Product and Questions",
    "permalink": "/docs/api-reference/product-and-questions"
  },
  "next": {
    "title": "Product Configs",
    "permalink": "/docs/api-reference/config"
  }
};


/* @jsxRuntime classic */
/* @jsx mdx */

export const toc = [{
  value: 'Save client data',
  id: 'save-client-data',
  children: []
}, {
  value: 'Retrieve latest client data',
  id: 'retrieve-latest-client-data',
  children: []
}, {
  value: 'Retrieve data from specific point in time',
  id: 'retrieve-data-from-specific-point-in-time',
  children: []
}];

const layoutProps = {
  toc
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`All client information is stored under the tenant of the API_KEY owner.  Furthermore, all changes to
client information are tracked and archived, so that client data can be retrieved at any particular point
in time.`}</p>
    <p>{`This is done to enable input comparisons between times different quotes are generated, and even for
Errors and Omission (E&O) tracking purposes.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "clientId": "220c4020-7f20-4a16-ba51-d0945804dd83",
  "clientName": "Business Name",
  "clientType": "COMMERCIAL",
  "locationCode": "HQ",
  "clientData": {
    "client.name": {
      "type": "ANSWER",
      "answer": "Business Name",
      "qid": "client.name"
    },
    "client.phoneNumber": {
      "type": "ANSWER",
      "answer": "3121234567",
      "qid": "client.phoneNumber"
    }
    "client.industry.naics": {
      "type": "ANSWER",
      "answer": "451130",
      "qid": "client.industry.naics"
    },
    "addresses": {
      "type": "LOOP",
      "qid": "addresses",
      "iterations": [
        {
          "iterationId": "f4c41c8d-ffb6-4883-902b-ab6357fc1a54",
          "answers": {
            "client.address1": {
              "type": "ANSWER",
              "answer": "123 Main St",
              "qid": "client.address1"
            },
            "client.city": {
              "type": "ANSWER",
              "answer": "Chicago",
              "qid": "client.city"
            },
            "client.state": {
              "type": "ANSWER",
              "answer": "IL",
              "qid": "client.state"
            },
            "client.zipCode": {
              "type": "ANSWER",
              "answer": "60089",
              "qid": "client.zipCode"
            }
          }
        }
      ]
    }
  }
}
`}</code></pre>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`clientId`}</inlineCode>{`: Each client is assigned a unique clientId.  If `}<inlineCode parentName="li">{`clientId`}</inlineCode>{` is specified, then it is expected that you
are attempting to update an existing client.  If a specified client is not found using this `}<inlineCode parentName="li">{`clientId`}</inlineCode>{` a `}<strong parentName="li">{`404:Not Found`}</strong>{`
error is returned.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`clientType`}</inlineCode>{`: Specify if client is `}<inlineCode parentName="li">{`COMMERCIAL`}</inlineCode>{` or `}<inlineCode parentName="li">{`PERSONAL`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`locationCode`}</inlineCode>{`: If your organization uses locationCodes, this is where you can specify it for a client`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`clientData`}</inlineCode>{`: Array of answer data`}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`qid`}</inlineCode>{`: Question identifier (qid) as per `}<a parentName="li" {...{
              "href": "/docs/api-reference/product-and-questions"
            }}>{`question API`}</a></li>
          <li parentName="ul"><inlineCode parentName="li">{`answer`}</inlineCode>{`: Answer for the question`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`type`}</inlineCode>{`: Specify if element is LOOP or ANSWER`}</li>
        </ul>
      </li>
    </ul>
    <h3 {...{
      "id": "save-client-data"
    }}>{`Save client data`}</h3>
    <p><strong parentName="p">{`PUT:`}</strong>{` `}<inlineCode parentName="p">{`https://api.{ENV}.dais.com/ioi/v3/client`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-curl"
      }}>{`curl --location --request PUT 'https://api.{ENV}.dais.com/ioi/v3/client' \\
--header 'Authorization: Basic <yourAuthHere>' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "clientId": "<clientId>",
    "clientName": "<clientName>",
    "clientType": "COMMERCIAL",
    "locationCode": "<locationCode>",
    "clientData": {
      "<qid>": {
        "type": "ANSWER",
        "answer": "<answer>",
        "qid": "<qid>"
      }
}'
`}</code></pre>
    <p>{`Post all client data to this endpoint to save it.  If `}<inlineCode parentName="p">{`clientId`}</inlineCode>{` is not specified, it is assumed you are creating
a new client.  If it is specified, and a client with that id exists, then data provided will clobber any existing
data for that client.`}</p>
    <h3 {...{
      "id": "retrieve-latest-client-data"
    }}>{`Retrieve latest client data`}</h3>
    <p><strong parentName="p">{`GET:`}</strong>{` `}<inlineCode parentName="p">{`https://api.{ENV}.dais.com/ioi/v3/client/{client_id}/product/{product_id}`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-curl"
      }}>{`curl --location --request GET 'https://api.{ENV}.dais.com/ioi/v3/client/<clientId>/product/<productId>' \\
--header 'Authorization: Basic <yourAuthHere>'
`}</code></pre>
    <p>{`Get client data by specifying a `}<inlineCode parentName="p">{`clientId`}</inlineCode>{`.  If client is not found, `}<strong parentName="p">{`HTTP 404`}</strong>{` is returned. `}</p>
    <h3 {...{
      "id": "retrieve-data-from-specific-point-in-time"
    }}>{`Retrieve data from specific point in time`}</h3>
    <p><strong parentName="p">{`GET:`}</strong>{` `}<inlineCode parentName="p">{`https://api.{ENV}.dais.com/ioi/v3/client/{client_id}/product/{product_id}/datetime/{date}`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-curl"
      }}>{`curl --location --request GET 'https://api.{ENV}.dais.com/ioi/v3/client/<clientId>/product/<productId>/datetime/<date>' \\
--header 'Authorization: Basic <yourAuthHere>'
`}</code></pre>
    <p>{`To get the state of client data at a specific point in time, specify `}<inlineCode parentName="p">{`date`}</inlineCode>{`
in `}<strong parentName="p">{`ISO-8601 format`}</strong>{` (i.e. `}<inlineCode parentName="p">{`2020-12-14T20:27:56.812Z`}</inlineCode>{`)`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;
